import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Reveal } from "@/components/motion";
import Seo, { SITE_URL } from "@/components/Seo";
import { fmtDate } from "@/pages/Blog";

const MD_SPLIT = /(!\[[^\]]*\]\([^)]+\)|\[[^\]]+\]\([^)]+\))/g;

function inlineNodes(text, keyBase) {
  return text.split(MD_SPLIT).filter(Boolean).map((part, i) => {
    const img = part.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (img)
      return (
        <img
          key={`${keyBase}-img${i}`}
          src={img[2]}
          alt={img[1]}
          loading="lazy"
          className="my-8 w-full rounded-2xl object-cover"
          data-testid="blog-inline-image"
        />
      );
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const [, txt, href] = link;
      return href.startsWith("/") ? (
        <Link key={`${keyBase}-l${i}`} to={href} className="font-semibold text-brand underline underline-offset-2 hover:text-ink">
          {txt}
        </Link>
      ) : (
        <a key={`${keyBase}-a${i}`} href={href} target="_blank" rel="noopener noreferrer" className="font-semibold text-brand underline underline-offset-2 hover:text-ink">
          {txt}
        </a>
      );
    }
    return <span key={`${keyBase}-t${i}`}>{part}</span>;
  });
}

function Body({ body }) {
  return body.split("\n\n").map((block, i) => {
    const t = block.trim();
    if (t.startsWith("## ")) return <h2 key={i} className="mt-10 font-display text-2xl font-extrabold text-ink">{inlineNodes(t.slice(3), `h${i}`)}</h2>;
    if (t.split("\n").every((l) => l.trim().startsWith("- ")))
      return (
        <ul key={i} className="mt-5 list-disc space-y-2 pl-6 text-base leading-relaxed text-slate-600">
          {t.split("\n").map((l, j) => (
            <li key={j}>{inlineNodes(l.trim().slice(2), `b${i}-${j}`)}</li>
          ))}
        </ul>
      );
    if (t.startsWith("![")) return inlineNodes(t, `p${i}`);
    return <p key={i} className="mt-5 text-base leading-relaxed text-slate-600">{inlineNodes(t, `p${i}`)}</p>;
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api
      .get(`/posts/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <section className="container-x py-32 text-center" data-testid="blog-post-notfound">
        <h1 className="font-display text-3xl font-extrabold text-ink">This article could not be found</h1>
        <p className="mt-4 text-slate-600">It may have been unpublished or the link is outdated.</p>
        <Link to="/blog" className="btn-primary mt-8 inline-flex">Back to the blog</Link>
      </section>
    );

  if (!post)
    return <p className="container-x py-32 text-center text-sm text-slate-500" data-testid="blog-post-loading">Loading…</p>;

  const jsonld = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.published_at,
    dateModified: post.updated_at,
    image: `${SITE_URL}/og-image.png`,
    author: { "@type": "Organization", name: "Eletech Global Ontario School", url: `${SITE_URL}/` },
    publisher: { "@type": "Organization", name: "Eletech Global Ontario School", url: `${SITE_URL}/` },
    mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
  };

  return (
    <>
      <Seo
        title={`${post.title} | EGOS Blog`}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        jsonld={jsonld}
      />
      <section className="container-x py-16 sm:py-24" data-testid="blog-post-page">
        <Reveal>
          <div className="mx-auto max-w-3xl">
            <Link to="/blog" className="font-mono text-xs uppercase tracking-[0.2em] text-brand hover:text-ink">
              ← All articles
            </Link>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <span className="chip chip-active">{post.category}</span>
              <span className="text-xs text-slate-400" data-testid="blog-post-date">{fmtDate(post.published_at)}</span>
            </div>
            <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight text-ink sm:text-5xl">{post.title}</h1>
            <p className="mt-5 border-l-2 border-brand/40 pl-5 font-serif text-lg italic text-slate-600">{post.excerpt}</p>
            {post.coverImage && (
              <img
                src={post.coverImage}
                alt={post.title}
                className="mt-8 aspect-video w-full rounded-2xl object-cover"
                data-testid="blog-post-cover"
              />
            )}
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <article className="mx-auto mt-8 max-w-3xl" data-testid="blog-post-body">
            <Body body={post.body} />
          </article>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="card-line mx-auto mt-16 max-w-3xl bg-ink p-8 text-center sm:p-10" data-testid="blog-post-cta">
            <h2 className="font-display text-2xl font-extrabold text-ivory">Ready to start your OSSD journey?</h2>
            <p className="mt-3 text-sm text-ivory/70">Ministry-inspected credits, Ontario Certified Teachers, 100% online.</p>
            <Link to="/admissions" className="btn-accent mt-7 inline-flex">Apply now</Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
